import React, { Suspense, useCallback, useMemo, useRef } from 'react';
import { Popover, Typography } from '@mui/material';
import Box from '@components/Atoms/Box';
import { StandardRecord } from '@CT-Types/lessonDomain';
import MultiSelectTree from '@components/Atoms/MultiSelectTree';
import HighlightSelector from './HighlightSelector';
import { useContentFilterStandardContext } from '../ContentFilterProvider';
import { buildTags } from '../utils';

const popOverSlotProps = {
  sx: {
    width: 'auto',
    maxWidth: '40rem',
    maxHeight: 400,
    mt: 0.5,
    boxShadow:
      '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
  },
};

interface StandardPopoverProps {
  open: boolean;
  anchor: HTMLElement | null;
  onClose: () => void;
  disabled: boolean;
}

const StandardPopover: React.FC<StandardPopoverProps> = ({
  open,
  anchor,
  onClose,
  disabled,
}) => {
  const paperRef = useRef(null);
  const {
    selectedStandards,
    priorityStandards,
    recommendedStandards,
    manualSelectedStandards,
    removedStandards,
    setRemovedStandards,
    setSelectedStandards,
    setManuallySelectedStandards,
    getStandardTree,
    getStandardById,
  } = useContentFilterStandardContext();

  const getRecommendedStandardObjects = useMemo(
    () => () => {
      const priorityIds = Object.keys(priorityStandards).map(Number);
      const recommendedIds = Object.keys(recommendedStandards).map(Number);
      const standardsToHighlight = [
        ...priorityIds,
        ...recommendedIds.filter((id) => !priorityIds.includes(id)),
      ];
      return standardsToHighlight
        .filter((id) => !selectedStandards.includes(id))
        .map((id) => getStandardById(id))
        .filter(Boolean);
    },
    [getStandardById, priorityStandards, recommendedStandards, selectedStandards],
  );

  // when a user selects a recommended standard, add it to the selected standards
  // and keep track of the manual selections separately
  const handleSelectRecommended = (id: number) => {
    setManuallySelectedStandards([...manualSelectedStandards, id]);
    setSelectedStandards([...selectedStandards, id]);
    setRemovedStandards(removedStandards.filter((removedId) => removedId !== id));
  };

  // when the user changes the selected standards, update the selectedStandards and manualSelectedStandards
  const handleStandardChange = (standards: number[]) => {
    const newStandards = standards.filter(
      (id) => !manualSelectedStandards.includes(id) && !selectedStandards.includes(id),
    );

    const existingManual = manualSelectedStandards.filter((id) => standards.includes(id));
    const removed = selectedStandards.filter((id) => !standards.includes(id));

    setManuallySelectedStandards([...existingManual, ...newStandards]);
    setSelectedStandards(standards);

    // only keep record if a single standard was removed at a time
    if (removed.length === 1) {
      setRemovedStandards([...removedStandards, ...removed]);
    }
  };

  const getStandardTags = useCallback(() => {
    const tooltipHeader: Record<string, React.ReactNode> = {};

    const createTooltipHeader = (standard: StandardRecord | null) => {
      if (!standard?.section && !standard?.subsection && !standard?.description) {
        return null;
      }

      return (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              display: 'block',
              mb: 1,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              maxWidth: '600px',
            }}
          >
            {standard.description}
          </Typography>
          <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 1 }} />
          <Typography
            variant="h5"
            sx={{
              display: 'block',
              mb: 0.5,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              maxWidth: '600px',
            }}
          >
            {standard.section}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              display: 'block',
              mb: 0.5,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              maxWidth: '600px',
            }}
          >
            {standard.subsection}
          </Typography>
          <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 1 }} />
        </Box>
      );
    };

    const addTooltipsForTree = (nodes: any[]) => {
      nodes.forEach((node) => {
        if (node.standard) {
          const header = createTooltipHeader(node.standard);
          if (header) {
            tooltipHeader[node.id] = header;
          }
        }

        if (node.children) {
          addTooltipsForTree(node.children);
        }
      });
    };

    const tree = getStandardTree();
    addTooltipsForTree(tree);

    manualSelectedStandards.forEach((id) => {
      const standard = getStandardById(id);
      const header = createTooltipHeader(standard);
      if (header) {
        tooltipHeader[id.toString()] = header;
      }
    });
    Object.keys(priorityStandards).forEach((id) => {
      const standard = getStandardById(Number(id));
      const header = createTooltipHeader(standard);
      if (header) {
        tooltipHeader[id] = header;
      }
    });

    Object.keys(recommendedStandards).forEach((id) => {
      const standard = getStandardById(Number(id));
      const header = createTooltipHeader(standard);
      if (header) {
        tooltipHeader[id] = header;
      }
    });

    const tags = buildTags('standard', {
      auto: {
        source: 'Lessons',
        data: priorityStandards,
      },
      manual: manualSelectedStandards,
      recommend: recommendedStandards,
      tooltipHeader,
    });

    return tags;
  }, [
    priorityStandards,
    recommendedStandards,
    getStandardById,
    manualSelectedStandards,
    getStandardTree,
  ]);

  return (
    <Popover
      id="standard-filter-popover"
      open={open}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          ref: paperRef,
          ...popOverSlotProps,
        },
      }}
    >
      <Box sx={{ p: 1, width: 'auto', minWidth: 'auto', maxWidth: '60rem' }}>
        <Suspense fallback={<Box sx={{ p: 2 }}>Loading...</Box>}>
          <HighlightSelector
            items={getRecommendedStandardObjects()}
            getItemId={(standard: StandardRecord) => standard.id}
            getItemLabel={(standard: StandardRecord) =>
              `${standard.code}: ${standard.description}`
            }
            primaryText="Based on the selected Lesson(s), here are ChalkTalk's recommended Standards:"
            secondaryText="Currently Selected Standards (Click to add more):"
            onSelect={(standard: StandardRecord) =>
              handleSelectRecommended(Number(standard.id))
            }
            disabled={disabled}
            tags={getStandardTags()}
          >
            <MultiSelectTree
              id="standard-filter-tree"
              tree={getStandardTree()}
              onChange={handleStandardChange}
              placeholder="Select Standards..."
              selected={selectedStandards}
              disabled={disabled}
              tags={getStandardTags()}
              parentRef={paperRef}
            />
          </HighlightSelector>
        </Suspense>
      </Box>
    </Popover>
  );
};

export default StandardPopover;
